interface GalleryArtProps {
  id: string
  className?: string
}

export function GalleryArt({ id, className = '' }: GalleryArtProps) {
  return (
    <svg
      viewBox="0 0 400 300"
      className={`h-full w-full ${className}`}
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={`bg-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbf9f3" />
          <stop offset="100%" stopColor="#f5f4ee" />
        </linearGradient>
        <linearGradient id={`accent-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#e67e22" />
          <stop offset="100%" stopColor="#944a00" />
        </linearGradient>
        <linearGradient id={`slate-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#c6e4f4" />
          <stop offset="100%" stopColor="#466270" />
        </linearGradient>
      </defs>

      <rect width="400" height="300" fill={`url(#bg-${id})`} />

      {id === 'cellular-autophagy' && (
        <>
          <circle cx="120" cy="100" r="45" fill="none" stroke="#466270" strokeWidth="1.5" />
          <circle cx="140" cy="90" r="18" fill="#c6e4f4" opacity="0.6" />
          <circle cx="100" cy="120" r="12" fill="#e67e22" opacity="0.5" />
          <path d="M160 130 Q200 100 240 140" fill="none" stroke="#944a00" strokeWidth="2" />
          <circle cx="260" cy="130" r="35" fill="none" stroke="#466270" strokeWidth="1.5" />
          <circle cx="270" cy="120" r="14" fill="#e67e22" opacity="0.5" />
          <path d="M90 190 L310 190" stroke="#dcc1b1" strokeWidth="1" strokeDasharray="4 4" />
          <text x="200" y="230" textAnchor="middle" fontSize="11" fill="#897365" fontFamily="Geist, sans-serif">Autophagy Pathway</text>
        </>
      )}

      {id === 'crispr-metrics' && (
        <>
          <rect x="60" y="160" width="40" height="80" fill="#c6e4f4" />
          <rect x="110" y="120" width="40" height="120" fill="#466270" opacity="0.8" />
          <rect x="160" y="140" width="40" height="100" fill="#e67e22" opacity="0.8" />
          <rect x="210" y="100" width="40" height="140" fill="#c6e4f4" />
          <rect x="260" y="130" width="40" height="110" fill="#466270" opacity="0.6" />
          <path d="M60 90 Q150 60 240 100 T340 80" fill="none" stroke="#944a00" strokeWidth="2" />
          <circle cx="150" cy="75" r="4" fill="#944a00" />
          <circle cx="240" cy="100" r="4" fill="#944a00" />
          <circle cx="330" cy="80" r="4" fill="#944a00" />
          <text x="200" y="40" textAnchor="middle" fontSize="11" fill="#897365" fontFamily="Geist, sans-serif">Editing Efficiency (%)</text>
        </>
      )}

      {id === 'neural-architecture' && (
        <>
          <circle cx="80" cy="80" r="22" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <circle cx="80" cy="150" r="22" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <circle cx="80" cy="220" r="22" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <circle cx="200" cy="110" r="26" fill="#c6e4f4" stroke="#466270" strokeWidth="1.5" />
          <circle cx="200" cy="190" r="26" fill="#c6e4f4" stroke="#466270" strokeWidth="1.5" />
          <circle cx="320" cy="80" r="22" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <circle cx="320" cy="150" r="22" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <circle cx="320" cy="220" r="22" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <line x1="102" y1="80" x2="174" y2="100" stroke="#944a00" strokeWidth="1.5" />
          <line x1="102" y1="150" x2="174" y2="120" stroke="#944a00" strokeWidth="1.5" />
          <line x1="102" y1="220" x2="174" y2="190" stroke="#944a00" strokeWidth="1.5" />
          <line x1="226" y1="110" x2="298" y2="90" stroke="#944a00" strokeWidth="1.5" />
          <line x1="226" y1="190" x2="298" y2="210" stroke="#944a00" strokeWidth="1.5" />
          <line x1="226" y1="120" x2="298" y2="150" stroke="#897365" strokeWidth="1" />
          <text x="200" y="260" textAnchor="middle" fontSize="11" fill="#897365" fontFamily="Geist, sans-serif">Layer-wise Connectivity</text>
        </>
      )}

      {id === 'protein-folding' && (
        <>
          <path
            d="M80 220 Q100 80 140 120 T200 100 T260 140 T320 80"
            fill="none"
            stroke={`url(#accent-${id})`}
            strokeWidth="12"
            strokeLinecap="round"
          />
          <path
            d="M80 220 Q100 80 140 120 T200 100 T260 140 T320 80"
            fill="none"
            stroke="#ffb783"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="8 8"
          />
          <circle cx="140" cy="120" r="8" fill="#466270" />
          <circle cx="200" cy="100" r="8" fill="#466270" />
          <circle cx="260" cy="140" r="8" fill="#466270" />
          <text x="200" y="260" textAnchor="middle" fontSize="11" fill="#897365" fontFamily="Geist, sans-serif">Folded Structure (pLDDT)</text>
        </>
      )}

      {id === 'quantum-superposition' && (
        <>
          <circle cx="200" cy="150" r="70" fill="none" stroke="#466270" strokeWidth="1" />
          <circle cx="200" cy="150" r="45" fill="none" stroke="#e67e22" strokeWidth="2" />
          <circle cx="200" cy="150" r="20" fill="#c6e4f4" opacity="0.6" />
          <line x1="200" y1="80" x2="200" y2="220" stroke="#897365" strokeWidth="1" />
          <line x1="130" y1="150" x2="270" y2="150" stroke="#897365" strokeWidth="1" />
          <line x1="150" y1="100" x2="250" y2="200" stroke="#944a00" strokeWidth="1.5" />
          <line x1="250" y1="100" x2="150" y2="200" stroke="#944a00" strokeWidth="1.5" />
          <circle cx="200" cy="150" r="5" fill="#944a00" />
          <text x="200" y="260" textAnchor="middle" fontSize="11" fill="#897365" fontFamily="Geist, sans-serif">Superposition State</text>
        </>
      )}

      {id === 'ai4s-workflow-map' && (
        <>
          <rect x="50" y="110" width="80" height="50" rx="4" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <rect x="160" y="80" width="80" height="50" rx="4" fill="#c6e4f4" stroke="#466270" strokeWidth="1.5" />
          <rect x="160" y="160" width="80" height="50" rx="4" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <rect x="270" y="110" width="80" height="50" rx="4" fill="#f5f4ee" stroke="#466270" strokeWidth="1.5" />
          <line x1="130" y1="135" x2="160" y2="105" stroke="#944a00" strokeWidth="2" />
          <line x1="130" y1="135" x2="160" y2="185" stroke="#944a00" strokeWidth="2" />
          <line x1="240" y1="105" x2="270" y2="135" stroke="#944a00" strokeWidth="2" />
          <line x1="240" y1="185" x2="270" y2="135" stroke="#944a00" strokeWidth="2" />
          <text x="90" y="140" textAnchor="middle" fontSize="9" fill="#466270" fontFamily="Geist, sans-serif">Literature</text>
          <text x="200" y="110" textAnchor="middle" fontSize="9" fill="#466270" fontFamily="Geist, sans-serif">Hypothesis</text>
          <text x="200" y="190" textAnchor="middle" fontSize="9" fill="#466270" fontFamily="Geist, sans-serif">Experiment</text>
          <text x="310" y="140" textAnchor="middle" fontSize="9" fill="#466270" fontFamily="Geist, sans-serif">Figure</text>
          <text x="200" y="260" textAnchor="middle" fontSize="11" fill="#897365" fontFamily="Geist, sans-serif">AI4S Pipeline</text>
        </>
      )}
    </svg>
  )
}
